import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { parseMutationError } from '../api/errors';
import {
  type ConsentType,
  useAddTextVersion,
  useConsentTypes,
  useCreateConsentType,
  useDeleteConsentType,
  useUpdateConsentType,
} from '../api/consent';
import {
  NON_DEFAULT_LOCALES,
  type LocalizedString,
  type SupportedLocale,
  joinFromColumns,
  localizedFromTextVersions,
  splitForColumns,
} from '../api/localized-string';
import { LocalizedStringInput } from '../components/localized-string-input';

export function OrgConsentAdditions({ orgId, productId }: { orgId: string; productId: string }) {
  const additionsQuery = useConsentTypes(productId, orgId);
  const create = useCreateConsentType(productId);
  const update = useUpdateConsentType(productId);
  const addVersion = useAddTextVersion(productId);
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState<LocalizedString>({ default: '' });
  const [blocking, setBlocking] = useState(false);
  const [text, setText] = useState<LocalizedString>({ default: '' });
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<ConsentType | null>(null);
  const [editName, setEditName] = useState<LocalizedString>({ default: '' });
  const [editBlocking, setEditBlocking] = useState(false);
  const [editText, setEditText] = useState<LocalizedString>({ default: '' });
  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false);

  const remove = useDeleteConsentType(productId);
  const [removing, setRemoving] = useState<ConsentType | null>(null);
  const [removeOpen, { open: openRemove, close: closeRemove }] = useDisclosure(false);

  function startRemove(t: ConsentType) {
    setRemoving(t);
    openRemove();
  }

  function confirmRemove() {
    if (!removing) return;
    remove.mutate(removing.id, {
      onSuccess: () => {
        notifications.show({ color: 'green', message: 'Consent removed.' });
        closeRemove();
      },
      onError: (err) =>
        notifications.show({
          color: 'red',
          message: parseMutationError(
            err,
            "Can't remove — consent has already been recorded for patients.",
          ),
        }),
    });
  }

  const additions = (additionsQuery.data ?? []).filter(
    (t) => t.organisation_id === orgId && t.active,
  );

  function startEdit(t: ConsentType) {
    setEditing(t);
    setEditName(joinFromColumns(t.display_name, t.display_name_translations));
    setEditBlocking(t.blocking);
    setEditText(localizedFromTextVersions(t.text_versions, orgId));
    openEdit();
  }

  function saveEdit() {
    if (!editing) return;
    const editingType = editing;
    const { default: display_name, translations: display_name_translations } =
      splitForColumns(editName);
    const nameChanged =
      display_name !== editingType.display_name ||
      JSON.stringify(display_name_translations ?? {}) !==
        JSON.stringify(editingType.display_name_translations ?? {});
    const currentText = localizedFromTextVersions(editingType.text_versions, orgId);
    const locales: SupportedLocale[] = ['en', ...NON_DEFAULT_LOCALES];

    // Each new text version's number is computed server-side from the current
    // max, so the per-locale writes must be serialised — firing them
    // concurrently would race on the [type, org, version] unique constraint.
    const save = async () => {
      if (nameChanged || editBlocking !== editingType.blocking) {
        await update.mutateAsync({
          id: editingType.id,
          display_name,
          display_name_translations,
          blocking: editBlocking,
        });
      }
      for (const locale of locales) {
        const nextBody =
          locale === 'en' ? editText.default : (editText.translations?.[locale] ?? '');
        const prevBody =
          locale === 'en' ? currentText.default : (currentText.translations?.[locale] ?? '');
        // Only publish non-empty, changed bodies; clearing a saved
        // translation is a deliberate no-op (see design doc).
        if (nextBody !== '' && nextBody !== prevBody) {
          await addVersion.mutateAsync({
            id: editingType.id,
            body: nextBody,
            locale,
            organisation_id: orgId,
          });
        }
      }
    };

    save()
      .then(() => {
        notifications.show({ color: 'green', message: 'Consent updated.' });
        closeEdit();
      })
      .catch(() => notifications.show({ color: 'red', message: 'Could not save consent.' }));
  }

  function submit() {
    const { default: display_name, translations: display_name_translations } =
      splitForColumns(displayName);
    const { default: textBody, translations: text_translations } = splitForColumns(text);
    create.mutate(
      {
        product_id: productId,
        organisation_id: orgId,
        code,
        display_name,
        display_name_translations,
        blocking,
        text: textBody,
        text_translations,
        locale: 'en',
      },
      {
        onSuccess: () => {
          notifications.show({ color: 'green', message: 'Consent added.' });
          setCode('');
          setDisplayName({ default: '' });
          setBlocking(false);
          setText({ default: '' });
          setError(null);
        },
        onError: (err) => setError(parseMutationError(err, 'Could not add consent.')),
      },
    );
  }

  return (
    <Stack gap="md">
      <Title order={4}>Organisation-specific consents</Title>
      {error && <Alert color="red">{error}</Alert>}

      <Card withBorder padding="lg">
        {additions.length === 0 ? (
          <Text c="dimmed">No organisation-specific consents.</Text>
        ) : (
          <Table withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Code</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Flavour</Table.Th>
                <Table.Th>Text</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {additions.map((t) => (
                <Table.Tr key={t.id}>
                  <Table.Td>{t.code}</Table.Td>
                  <Table.Td>{t.display_name}</Table.Td>
                  <Table.Td>
                    <Badge color={t.blocking ? 'red' : 'gray'} variant="light">
                      {t.blocking ? 'blocking' : 'non-blocking'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={2}>
                      {localizedFromTextVersions(t.text_versions, orgId).default || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Button size="xs" variant="subtle" onClick={() => startEdit(t)}>
                        Edit
                      </Button>
                      <Button size="xs" variant="subtle" color="red" onClick={() => startRemove(t)}>
                        Remove
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Card withBorder padding="lg" maw={520}>
        <Stack gap="sm">
          <Title order={5}>Add organisation consent</Title>
          <TextInput label="Code" value={code} onChange={(e) => setCode(e.currentTarget.value)} />
          <LocalizedStringInput
            label="Display name"
            value={displayName}
            onChange={setDisplayName}
          />
          <Switch
            label="Blocking (patient cannot proceed without it)"
            checked={blocking}
            onChange={(e) => setBlocking(e.currentTarget.checked)}
          />
          <LocalizedStringInput
            label="Consent text"
            description="Shown to patients. Required."
            multiline
            value={text}
            onChange={setText}
          />
          <Group justify="flex-end">
            <Button
              onClick={submit}
              loading={create.isPending}
              disabled={!code || !displayName.default || !text.default}
            >
              Add consent
            </Button>
          </Group>
        </Stack>
      </Card>

      <Modal opened={editOpen} onClose={closeEdit} title={`Edit ${editing?.code ?? ''}`}>
        <Stack>
          <LocalizedStringInput
            label="Display name"
            value={editName}
            onChange={setEditName}
            required
          />
          <Switch
            label="Blocking"
            checked={editBlocking}
            onChange={(e) => setEditBlocking(e.currentTarget.checked)}
          />
          <LocalizedStringInput
            label="Consent text"
            multiline
            value={editText}
            onChange={setEditText}
            required
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeEdit}>
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              loading={update.isPending || addVersion.isPending}
              disabled={!editName.default || !editText.default}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={removeOpen} onClose={closeRemove} title={`Remove ${removing?.code ?? ''}`}>
        <Stack>
          <Text>
            Remove this organisation-specific consent? Patients will no longer be asked for it.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeRemove}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmRemove} loading={remove.isPending}>
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
